import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { WorkflowContext } from '../contexts';
import { useModal } from '../contexts';
import { CheckIcon, ChevronDownIcon, ExternalLinkIcon, PencilIcon, UserGroupIcon } from '@heroicons/react/outline';
import { putData } from '../backend';
import { Workflow } from '../interfaces';
import moment from 'moment-timezone';
import HeroIcon from './HeroIcon';
import WorkflowModalEdit from './WorkflowModalEdit';
import WorkflowModalShare from './WorkflowModalShare';
import WorkflowOutputFolderModal from './WorkflowOutputFolderModal';
import WorkflowOutputOneDriveModal from './WorkflowOutputOneDriveModal';
import WorkflowOutputHubspotModal from './WorkflowOutputHubspotModal';
import PayWallModalTeams from './PayWallModalTeams';
import ErrorModal from './ErrorModal';
import WorkflowAutomationPanelSheets from './WorkflowAutomationPanelSheets';
import WorkflowAutomationPanelForms from './WorkflowAutomationPanelForms';
import WorkflowAutomationPanelHubspot from './WorkflowAutomationPanelHubspot';
import WorkflowAutomationPanelXero from './WorkflowAutomationPanelXero';
import WorkflowAutomationPanelWebhook from './WorkflowAutomationPanelWebhook';
import WorkflowAutomationPanelExcel from './WorkflowAutomationPanelExcel';
import WorkflowAutomationPanelCopper from './WorkflowAutomationPanelCopper';
import WorkflowAutomationPanelSignatureLink from './WorkflowAutomationPanelSignatureLink';
import WorkflowAutomationPanelManual from './WorkflowAutomationPanelManual';
import WorkflowAiEnrichmentSettings from './WorkflowAiEnrichmentSettings';
import useUser from '../hooks/useUser';
import { useTranslation } from 'react-i18next';

interface Props {
  defaultShowOutputLocations?: boolean;
  setSourceSettingsRef?: (node: HTMLDivElement | null) => void;
}

function WorkflowSidebar(props: Props) {
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();
  const { user, hasPermission } = useUser();

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const { pathname, key: locationKey } = useLocation();

  const canEdit = hasPermission(workflow.team, "workflows.edit");
  const [showOutputLocations, setShowOutputLocations] = useState(props.defaultShowOutputLocations ?? false);
  const [showSourceSettings, setShowSourceSettings] = useState(true);

  const aiEnrichmentRequested = pathname.includes("ai-enrichment");
  const [showAiEnrichment, setShowAiEnrichment] = useState(aiEnrichmentRequested);

  useEffect(() => {
    if (aiEnrichmentRequested)
      setShowAiEnrichment(true);
  }, [aiEnrichmentRequested, locationKey]);

  function openEditModal() {
    if (canEdit) {
      openModal(<WorkflowModalEdit workflow={workflow} updateWorkflow={(w: Workflow) => {
        const updatedAt = moment().toISOString();
        const lastEditedBy = workflow.team?.members?.find(u => u.id === user?.id) ?? workflow.lastEditedBy;
        mutateWorkflow({ ...w, updatedAt, lastEditedBy }, false);
        putData(`/workflows/${workflow.id}/`, w)
          .then(mutateWorkflow)
          .catch((err) => openModal(<ErrorModal details={err} />));
        closeModal();
      }} />);
    }
  }

  return (
    <div className="flex-shrink-0 w-[360px] border-l border-gray-200 bg-white h-full flex flex-col">
      <div className='p-4 pb-2 border-gray-200 border-b flex flex-col gap-2'>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-10 h-10 text-white rounded flex-shrink-0" style={{ backgroundColor: workflow.color }}>
            <HeroIcon icon={workflow.icon} className="w-6 h-6" />
          </div>
          <div className='min-w-0 flex-1 flex flex-col'>
            <div className="font-gilroy font-semibold text-gray-600 truncate">
              {workflow.name}
            </div>
            <div className="font-gilroy font-semibold text-gray-400 text-xs w-40">
              {moment(workflow.updatedAt).fromNow()}
            </div>
          </div>
          {canEdit &&
            <button className="text-gray-400 hover:text-blue transition-colors flex-shrink-0" onClick={openEditModal}>
              <PencilIcon className="w-4 h-4" />
            </button>
          }
          <div className="relative select-none flex-shrink-0">
            {!!workflow.team &&
              <div className="absolute w-4 h-4 rounded-full shadow bg-green border-white border-2 -right-1.5 -top-1.5 z-10">
                <CheckIcon className="h-3 w-3 text-white" />
              </div>
            }
            <button
              className="flex items-center bg-blue-50 shadow hover:shadow-inner text-blue font-gilroy font-semibold text-xs hover:underline px-2 py-1 rounded-full"
              onClick={() => user?.subscription?.featureTeams ? openModal(<WorkflowModalShare workflow={workflow} />) : openModal(<PayWallModalTeams />)}
            >
              <UserGroupIcon className="w-3.5 h-3.5 mr-1" /> {t("Share")}
            </button>
          </div>
        </div>
        <div className="text-gray-600 text-sm w-full line-clamp-2 hover:line-clamp-none cursor-default">
          {workflow.description}
        </div>
        {workflow.bluebirdEligible && user?.subscription?.featureSalesAgent && (
          <div className="text-xs bg-purple/10 text-gray-600 rounded-sm px-3 py-2">
            <span className="text-blue font-semibold">Context Hub</span><br />
            This workflow is selectable by the Portant Context Hub.
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-scroll p-4 flex-shrink-0 pb-20">
        {workflow.source?.sourceType === "GOOGLE_SHEETS" &&
          <WorkflowAutomationPanelSheets />
        }
        {workflow.source?.sourceType === "GOOGLE_FORMS" &&
          <WorkflowAutomationPanelForms />
        }
        {workflow.source?.sourceType === "HUBSPOT" &&
          <WorkflowAutomationPanelHubspot />
        }
        {workflow.source?.sourceType === "COPPER" &&
          <WorkflowAutomationPanelCopper />
        }
        {workflow.source?.sourceType === "XERO" &&
          <WorkflowAutomationPanelXero />
        }
        {workflow.source?.sourceType === "MICROSOFT_EXCEL" &&
          <WorkflowAutomationPanelExcel />
        }
        {workflow.source?.sourceType === "WEBHOOK" &&
          <WorkflowAutomationPanelWebhook text={t("Portant will run this workflow whenever a new webhook event is received.")} />
        }
        {workflow.source?.sourceType === "TALLY" &&
          <WorkflowAutomationPanelWebhook text={t("Portant will run this workflow whenever a new response is submitted to Tally.")} />
        }
        {workflow.source?.sourceType === "GLIDE" &&
          <WorkflowAutomationPanelWebhook text={t("Portant will run this workflow whenever the selected action occurs in Glide.")} />
        }
        {workflow.source?.sourceType === "ZAPIER" &&
          <WorkflowAutomationPanelWebhook text={t("Portant will run this workflow whenever a zap is triggered in Zapier.")} />
        }
        {workflow.source?.sourceType === "SIGNATURE_LINK" &&
          <WorkflowAutomationPanelSignatureLink />
        }
        {workflow.source?.sourceType === "SIGNATURES" &&
          <WorkflowAutomationPanelManual />
        }
        {workflow.source?.sourceType === "DEBUG_JSON" &&
          <WorkflowAutomationPanelManual />
        }
        {canEdit && workflow.documents.length > 0 &&
          <>
            <div className="h-px bg-gray-200 my-4" />
            <div className="flex flex-col gap-3">
              <button
                className="flex items-center justify-between w-full font-gilroy font-semibold text-sm text-gray-600 cursor-pointer"
                onClick={() => setShowOutputLocations(b => !b)}
              >
                {t("Output Locations")}
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${showOutputLocations ? "rotate-180" : ""}`} />
              </button>
              {showOutputLocations && (workflow.outputFolder || workflow.documents.some(d => !d.documentType.startsWith("MICROSOFT"))) &&
                <div className="flex items-center gap-2 text-xs font-gilroy font-semibold text-gray-600">
                  <img src="https://static.portant.co/drive-icon.svg" className="w-5 h-5 flex-shrink-0" alt="Drive icon" />
                  {workflow.outputFolder ? (
                    <a className="group/link flex items-center gap-1 hover:text-blue hover:underline truncate" href={workflow.outputFolder.url} target="_blank" rel="noreferrer">
                      {workflow.outputFolder.name}
                      <ExternalLinkIcon className="w-3 h-3 flex-shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                    </a>
                  ) : (
                    <span className="text-gray-400">{t("Google Drive")}</span>
                  )}
                  <button className="ml-auto px-2 py-1 border border-gray-300 rounded text-xs font-gilroy font-semibold text-gray-600 hover:border-blue hover:text-blue cursor-pointer transition-all flex-shrink-0" onClick={() => openModal(<WorkflowOutputFolderModal workflow={workflow} mutateWorkflow={mutateWorkflow} />)}>
                    {t("Customize")}
                  </button>
                </div>
              }
              {showOutputLocations &&
                <div className="flex items-center gap-2 text-xs font-gilroy font-semibold text-gray-600">
                  <img src="https://static.portant.co/onedrive-icon.svg" className="w-5 h-5 flex-shrink-0" alt="OneDrive icon" />
                  {!workflow.onedriveDisabled && workflow.onedriveDriveName ? (
                    workflow.onedriveDriveId ? (
                      <a className="group/link flex items-center gap-1 hover:text-blue hover:underline truncate" href={`https://onedrive.live.com/?id=root&cid=${workflow.onedriveDriveId}`} target="_blank" rel="noreferrer">
                        {workflow.onedriveDriveName}
                        <ExternalLinkIcon className="w-3 h-3 flex-shrink-0 opacity-0 group-hover/link:opacity-100 transition-opacity" />
                      </a>
                    ) : (
                      <span className="truncate">{workflow.onedriveDriveName}</span>
                    )
                  ) : (
                    <span className="text-gray-400">{t("OneDrive/SharePoint")}</span>
                  )}
                  <button className="ml-auto px-2 py-1 border border-gray-300 rounded text-xs font-gilroy font-semibold text-gray-600 hover:border-blue hover:text-blue cursor-pointer transition-all flex-shrink-0" onClick={() => openModal(<WorkflowOutputOneDriveModal workflow={workflow} mutateWorkflow={mutateWorkflow} />)}>
                    {t("Customize")}
                  </button>
                </div>
              }
              {showOutputLocations && workflow.source?.sourceType === "HUBSPOT" &&
                <div className="flex items-center gap-2 text-xs font-gilroy font-semibold text-gray-600">
                  <img src={`${process.env.REACT_APP_STATIC_URL}/hubspot-icon.svg`} className="w-5 h-5 flex-shrink-0" alt="HubSpot icon" />
                  {workflow.documents.some(d => d.uploadToHubspot) ? (
                    <span className="truncate">{t("HubSpot")}</span>
                  ) : (
                    <span className="text-gray-400">{t("HubSpot")}</span>
                  )}
                  <button className="ml-auto px-2 py-1 border border-gray-300 rounded text-xs font-gilroy font-semibold text-gray-600 hover:border-blue hover:text-blue cursor-pointer transition-all flex-shrink-0" onClick={() => openModal(<WorkflowOutputHubspotModal workflow={workflow} mutateWorkflow={mutateWorkflow} />)}>
                    {t("Customize")}
                  </button>
                </div>
              }
            </div>
          </>
        }
        <div className="h-px bg-gray-200 my-4" />
        <div className="flex flex-col gap-3">
          <button
            className="flex items-center justify-between w-full font-gilroy font-semibold text-sm text-gray-600 cursor-pointer"
            onClick={() => setShowAiEnrichment(b => !b)}
          >
            {t("AI Enrichment")}
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${showAiEnrichment ? "rotate-180" : ""}`} />
          </button>
          {showAiEnrichment &&
            <WorkflowAiEnrichmentSettings />
          }
        </div>
        {pathname.endsWith("/source") &&
          <>
            <div className="h-px bg-gray-200 my-4" />
            <div className="flex flex-col gap-3">
              <button
                className="flex items-center justify-between w-full font-gilroy font-semibold text-sm text-gray-600 cursor-pointer"
                onClick={() => setShowSourceSettings(b => !b)}
              >
                {t("Source Settings")}
                <ChevronDownIcon className={`w-4 h-4 transition-transform ${showSourceSettings ? "rotate-180" : ""}`} />
              </button>
              {showSourceSettings &&
                <div ref={props.setSourceSettingsRef} className="flex flex-col gap-4" />
              }
            </div>
          </>
        }
      </div>
    </div>
  );
}

export default WorkflowSidebar;
